"use client"

import { useMemo, useState, type ReactNode } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readQuantity } from '@/lib/tools/numeric-input'

// Sources:
//  • DOE federal refrigerator energy-conservation standards (10 CFR 430).
//  • ENERGY STAR refrigerator key product criteria — certified models must
//    be at least 9–10% more efficient than the federal minimum.
//  • EIA average US residential electricity rate (most recent published): ~16.5¢/kWh.
//  • EPA eGRID national average CO2 emission factor: 0.86 lbs/kWh.
//  • EPA "Greenhouse Gases Equivalencies": one mature tree absorbs ~48 lbs CO2/yr.

const RELATED = [
  {
    title: "Refrigerator Cost of Ownership Calculator",
    category: "Energy & Cost",
    description: "True 10-year purchase + energy + maintenance cost.",
    href: "/tools/refrigerator-cost-of-ownership-calculator",
    time: "~45 sec",
  },
  {
    title: "Energy Cost Calculator",
    category: "Energy & Cost",
    description: "Annual operating cost for any fridge.",
    href: "/tools/energy-cost-calculator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Rebate Finder",
    category: "Energy & Cost",
    description: "Up to $500 back on ENERGY STAR upgrades.",
    href: "/tools/refrigerator-rebate-finder",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Energy Efficiency Calculator",
    category: "Energy & Cost",
    description: "Compare current vs replacement annual kWh.",
    href: "/tools/refrigerator-energy-efficiency-calculator",
    time: "~30 sec",
  },
]

export function EnergyStarSavingsCalculatorPageClient({ children }: { children?: ReactNode }) {
  const [currentKwh, setCurrentKwh] = useState("700")
  const [esKwh, setEsKwh] = useState("420")
  const [rate, setRate] = useState("16.5")

  const result = useMemo(() => {
    const current = readQuantity(currentKwh, { min: 50, max: 5000 })
    const es = readQuantity(esKwh, { min: 50, max: 5000 })
    const r = readQuantity(rate, { min: 1, max: 100 })
    if (current === null || es === null || r === null) return null

    const kwhSaved = Math.max(0, current - es)
    const annualSavings = (kwhSaved * r) / 100
    const tenYearSavings = annualSavings * 10
    const co2Reduction = kwhSaved * 0.86
    const treesEquivalent = Math.round(co2Reduction / 48)

    return { kwhSaved, annualSavings, tenYearSavings, co2Reduction, treesEquivalent }
  }, [currentKwh, esKwh, rate])

  return (
    <ToolPageShell
      toolName="Energy Star Savings Calculator"
      heroEyebrow="Energy & Cost"
      heroTitlePre="How much does ENERGY STAR"
      heroTitleItalic="actually"
      heroTitlePost="save you?"
      heroSubtitle="Compare your current fridge's annual kWh against an ENERGY STAR model — at your own electricity rate — to see annual dollars saved, 10-year savings, and the CO2 footprint cut."
      heroMeta={["⏱ ~30 sec", "DOE + ENERGY STAR + EIA + EPA data"]}
      heroShare={<ToolShareBar title="Energy Star Savings Calculator" />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "9–10%", label: "ENERGY STAR vs federal min", note: "ENERGY STAR criteria" },
            { value: "16.5¢", label: "US avg electricity rate", note: "EIA" },
            { value: "0.86", label: "lbs CO2 per kWh", note: "EPA eGRID" },
            { value: "48", label: "lbs CO2 / mature tree / yr", note: "EPA equivalencies" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={[
        {
          question: "Where do I find my fridge's annual kWh?",
          answer:
            "On the yellow EnergyGuide label, or in the manufacturer's spec sheet. Look for 'Estimated Yearly Electricity Use' or 'kWh/year'.",
        },
        {
          question: "What kWh range is typical for an ENERGY STAR model?",
          answer:
            "Modern ENERGY STAR full-size refrigerators typically draw 400–700 kWh/year. Compacts run lower; French-door units with through-the-door ice run higher. Check the ENERGY STAR certified product database for your size class.",
        },
        {
          question: "Why is ENERGY STAR meaningful if every new fridge meets the federal standard?",
          answer:
            "Federal standards (most recently updated in 2014) set a floor. ENERGY STAR certifies models that exceed that floor by at least 9–10%. Over a 10-year service life, that compounds into real dollars at typical US rates.",
        },
        {
          question: "Where does the CO2 number come from?",
          answer:
            "EPA eGRID reports a national-average grid emission factor of about 0.86 lbs CO2 per kWh. We multiply your annual kWh saved by that factor.",
        },
        {
          question: "What's the 'trees' equivalent?",
          answer:
            "EPA's Greenhouse Gas Equivalencies estimate that one mature tree absorbs roughly 48 lbs of CO2 per year. We divide your annual CO2 reduction by 48 to express the savings in tree-equivalent terms.",
        },
        {
          question: "Should I use my off-peak rate or my average rate?",
          answer:
            "Use your average. Take total cost ÷ total kWh from a recent bill — that captures fixed charges, tiered pricing, and seasonal variation more honestly than the on-peak rate alone.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "Ready to shop the fridges that pay back fastest?",
        body: "Browse ENERGY STAR–certified refrigerators sortable by capacity, type, and live retailer pricing.",
        buttonLabel: "Browse ENERGY STAR refrigerators",
        buttonHref: "/collections/energy-star-appliances",
      }}
    >
      {/* ============ CALCULATOR ============ */}
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            {/* Input card */}
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">
                  STEP 01
                </span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}
              >
                Old kWh.
                <br />
                ENERGY STAR kWh.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Both numbers come from the EnergyGuide label or the manufacturer&apos;s spec sheet.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label htmlFor="energy-star-current-kwh" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Current fridge — annual kWh
                  </label>
                  <div className="relative">
                    <input
                      id="energy-star-current-kwh"
                      type="number"
                      min={50}
                      max={5000}
                      step="1"
                      value={currentKwh}
                      onChange={(e) => setCurrentKwh(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      kWh/yr
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Older units (pre-2001) often above 1,000 kWh/yr
                  </div>
                </div>

                <div>
                  <label htmlFor="energy-star-model-kwh" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    ENERGY STAR model — annual kWh
                  </label>
                  <div className="relative">
                    <input
                      id="energy-star-model-kwh"
                      type="number"
                      min={50}
                      max={5000}
                      step="1"
                      value={esKwh}
                      onChange={(e) => setEsKwh(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      kWh/yr
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> ENERGY STAR full-size: ~400–700 kWh/yr
                  </div>
                </div>

                <div>
                  <label htmlFor="energy-star-electricity-rate" className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Electricity rate
                  </label>
                  <div className="relative">
                    <input
                      id="energy-star-electricity-rate"
                      type="number"
                      min={1}
                      max={100}
                      step="0.01"
                      value={rate}
                      onChange={(e) => setRate(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">
                      ¢/kWh
                    </span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> US avg per EIA: ~16.5¢
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            {/* Result panel */}
            <div className="flex flex-col">
              <div
                className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1"
                style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}
              >
                <div
                  aria-hidden
                  className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none"
                  style={{
                    background: "rgba(255,153,81,0.35)",
                    filter: "blur(60px)",
                    transform: "translate(25%, -35%)",
                  }}
                />
                <div className="relative">
                  <div className="flex items-center justify-between mb-8 flex-wrap gap-2">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70">
                      Annual savings
                    </div>
                    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-white/60">
                      <RefreshCw className="w-3 h-3" /> Updated live
                    </div>
                  </div>
                  <div
                    className="font-black"
                    style={{
                      fontSize: "clamp(36px, 8vw, 88px)",
                      letterSpacing: "-0.045em",
                      lineHeight: 0.9,
                    }}
                  >
                    {`$${result?.annualSavings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "0"}`}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">
                      /yr
                    </span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result && result.kwhSaved > 0
                      ? `${result.kwhSaved.toLocaleString("en-US", { maximumFractionDigits: 0 })} kWh saved per year — about $${result.tenYearSavings.toLocaleString("en-US", { maximumFractionDigits: 0 })} across a 10-year service life.`
                      : "Enter your current and ENERGY STAR kWh to see savings."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        10-yr savings
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {`$${result?.tenYearSavings.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"}`}
                      </div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">
                        CO2 cut / yr
                      </div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">
                        {result?.co2Reduction.toLocaleString("en-US", { maximumFractionDigits: 0 }) ?? "—"} lbs
                      </div>
                    </div>
                  </div>
                  {result && result.treesEquivalent > 0 && (
                    <p className="mt-4 text-[12px] text-white/70 leading-[1.5]">
                      Equivalent to {result.treesEquivalent} mature tree{result.treesEquivalent !== 1 ? "s" : ""} absorbing CO2 for a year (EPA equivalencies).
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FIRST CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Shop ENERGY STAR"
          title="Refrigerators with the lowest annual kWh"
          description="ENERGY STAR–certified models that exceed federal efficiency standards."
          collectionType="counter-depth"
          fallbackCollectionType="french-door"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                Under the hood
              </div>
              <h2
                className="text-gray-900 font-black"
                style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}
              >
                Two kWh values, <em className="italic text-[#ff9951]">one</em> rate.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Reference numbers come from DOE energy-conservation standards, the
                ENERGY STAR certified product database, EIA average residential rates,
                and EPA eGRID grid emission factors.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                {
                  n: "01",
                  t: "Annual savings = (current − ENERGY STAR) × rate",
                  d: "kWh saved per year, multiplied by your electricity rate, gives the honest annual dollar savings — no spread, no fees baked in.",
                },
                {
                  n: "02",
                  t: "10-year projection",
                  d: "Industry-typical refrigerator service life is ~10 years; we scale annual savings linearly across that span.",
                },
                {
                  n: "03",
                  t: "CO2 + tree equivalents",
                  d: "kWh saved × 0.86 lbs CO2/kWh (EPA eGRID national average), then ÷ 48 lbs/yr per mature tree (EPA Greenhouse Gas Equivalencies).",
                },
              ].map((s) => (
                <li
                  key={s.n}
                  className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900"
                >
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">
                    {s.n}
                  </div>
                  <div>
                    <h3
                      className="text-gray-900"
                      style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                    >
                      {s.t}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ============ SECOND CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="French door refrigerators worth the kWh"
          description="Premium designs with the strongest efficiency gains over older units."
          collectionType="french-door"
          fallbackCollectionType="counter-depth"
        />
      </section>
      {/* Server-rendered supporting data belongs inside the shell, before its footer. */}
      {children}
    </ToolPageShell>
  )
}
